import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';

import {
  Box,
  Card,
  Grid,
  IconButton,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Add, RemoveCircleOutline } from '@mui/icons-material';

import { client } from '@frontend/features/common/services';
import { QueryKeys } from '@frontend/features/common/types';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';
import { Table, usePagination } from '@frontend/features/common/components/Table';
import { useGetUsersByTeam } from '@frontend/features/users/queries';
import { useGetTeamById } from '@frontend/features/teams/queries';
import { LinkUserToTeam } from '@frontend/features/teams/components/LinkUserToTeam';
import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';
import { Link } from '@frontend/features/common/components/Link';

import { routes } from '../routes';

export const TeamView = () => {
  const { teamId } = useParams();
  const { t } = useTranslation();
  const { addToast } = useSnackBar();
  const queryClient = useQueryClient();
  const pagination = usePagination();
  const [search, setSearch] = useDebouncedState('', 300);
  const [isOpenLinkUser, setIsOpenLinkUser] = useState(false);
  const toggleOpenLinkUser = () => setIsOpenLinkUser((prev) => !prev);

  const { data } = useGetTeamById({
    id: Number(teamId),
  });

  const { data: users, isLoading } = useGetUsersByTeam({
    teamId: Number(teamId),
    pageSize: pagination.rowsPerPage,
    currentPage: pagination.page,
    search: search,
  });

  const handleRemoveFromTeam = async (userId: string) => {
    try {
      await client.delete(`/team/${teamId}/user/${userId}`);
      addToast({
        message: t('teams.linkUserToTeam.removed'),
        color: 'success',
      });
      queryClient.invalidateQueries([
        QueryKeys.getUsersByTeam,
        Number(teamId),
        pagination.page,
        pagination.rowsPerPage,
        search,
      ]);
    } catch (e) {
      addToast({
        message: t('teams.linkUserToTeam.removedFailed'),
        color: 'error',
      });
      console.log(e);
    }
  };

  const theme = useTheme();

  return (
    <Box display="flex" gap={4} flexDirection="column">
      <Box>
        <Typography variant="h6" color={theme.palette.text.primary}>
          {data?.name}
        </Typography>
        <Typography variant="subtitle1" align="left" color={theme.palette.text.disabled}>
          {data?.agencyName}
        </Typography>
      </Box>

      <Grid container>
        <Grid item xs={12}>
          <Card
            sx={{
              p: theme.spacing(2),
              boxShadow: 'none',
              borderRadius: 1,
            }}
            elevation={4}
          >
            <Box display="flex" alignItems="center" gap={theme.spacing(3)} marginBottom={theme.spacing(3)}>
              <Typography
                variant="subtitle1"
                align="left"
                color={theme.palette.text.primary}
                sx={{
                  flexGrow: 1,
                }}
              >
                {users?.count} {t('teams.members')}
              </Typography>

              <SearchInput placeholder="Buscar" onSearch={setSearch} />
              <ToggleButtonGroup
                exclusive
                sx={{
                  height: theme.spacing(5),
                  '& button': {
                    padding: theme.spacing(1),
                    width: theme.spacing(5),
                  },
                }}
              >
                <ToggleButton onClick={toggleOpenLinkUser} value="add">
                  <Add />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {teamId && (
              <LinkUserToTeam
                isOpen={isOpenLinkUser}
                onClose={toggleOpenLinkUser}
                teamId={Number(teamId)}
                queryKey={[QueryKeys.getUsersByTeam, Number(teamId), pagination.page, pagination.rowsPerPage, search]}
              />
            )}

            {users?.count !== undefined && users.count > 0 && (
              <Table
                columns={[
                  {
                    id: 'fullname',
                    label: t('teams.table.name'),
                    align: 'left',
                  },
                  {
                    id: 'email',
                    label: t('teams.table.email'),
                    align: 'left',
                  },

                  {
                    id: 'agencyName',
                    label: t('teams.table.agency'),
                    align: 'right',
                  },
                  { id: 'actions', label: '', align: 'left' },
                ]}
                data={{
                  key: 'id',
                  items: users.items.map((item) => ({
                    fullname: (
                      <Link to={routes.dashboard.userView.replace(':id', item.id)}>
                        {item.firstName} {item.lastName}
                      </Link>
                    ),
                    email: item.email,
                    agencyName: item.agencyName,
                    actions: (
                      <Tooltip title={t('teams.linkUserToTeam.removedTooltip')} placement="top">
                        <IconButton onClick={() => handleRemoveFromTeam(item.id)}>
                          <RemoveCircleOutline color="error" />
                        </IconButton>
                      </Tooltip>
                    ),
                  })),
                  totalCount: users.count,
                }}
                pagination={pagination}
                stickyHeader
              />
            )}

            {isLoading && <Skeleton height={theme.spacing(10)} variant="rounded" animation="wave" />}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
